/**
 * Holding screen shown to a freshly-signed-up Owner whose tenant is
 * still in `pending_approval`. Backend's withAuth blocks every route
 * except /me until the platform admin flips the status to 'trial', so
 * the UI here intentionally has nothing actionable beyond a refresh.
 */
import { Hourglass, RefreshCw, LogOut, Mail } from 'lucide-react';
import type { AuthProfile } from '@payroll/shared';
import { logout as liffLogout } from '../lib/liff';
import { useAuthStore } from '../lib/store';

export function PendingApprovalScreen({
  profile,
  onRefresh,
}: {
  profile: AuthProfile;
  onRefresh: () => void;
}) {
  const clear = useAuthStore((s) => s.clear);
  function handleLogout() {
    clear();
    try { liffLogout(); } catch { /* ignore */ }
    window.location.href = '/';
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-paper to-gold-wash">
      <div className="card max-w-sm w-full p-6 space-y-4">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gold-wash text-gold-deep mb-2">
            <Hourglass className="w-8 h-8" />
          </div>
          <h2 className="text-lg font-bold">บริษัทรอการอนุมัติ</h2>
          <p className="text-sm text-gray-600 mt-1">
            <strong>{profile.tenant_name}</strong>
          </p>
        </div>

        <div className="bg-gold-wash border border-gold/40 rounded-lg p-3 text-sm text-ink">
          <p className="font-medium mb-1">ขั้นตอนถัดไป</p>
          <p className="text-xs">
            ทีมงาน Payroll Edge กำลังตรวจสอบข้อมูล โดยปกติใช้เวลาไม่เกิน 1 วันทำการ
            <br />
            เมื่ออนุมัติแล้ว ระบบจะปลดล็อกอัตโนมัติและสามารถใช้งานได้ทันที
          </p>
        </div>

        {profile.tenant_code && (
          <div className="bg-white border border-gray-200 rounded-lg p-3 text-center">
            <div className="text-xs text-gray-500 uppercase tracking-wider">รหัสบริษัทของคุณ</div>
            <div className="text-2xl font-mono font-bold tracking-widest text-brand-700 mt-1">
              {profile.tenant_code}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              เก็บไว้ใช้แจกพนักงานหลังอนุมัติ
            </div>
          </div>
        )}

        <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-600">
          <div className="flex items-start gap-2">
            <Mail className="w-4 h-4 mt-0.5 shrink-0 text-gray-400" />
            <div>
              <div className="font-medium">ติดต่อสอบถาม</div>
              <div>ส่งข้อความใน LINE OA ของ Payroll Edge ได้ตลอด</div>
            </div>
          </div>
        </div>

        <button onClick={onRefresh} className="btn-primary w-full">
          <RefreshCw className="w-4 h-4" /> รีเฟรชสถานะ
        </button>
        <button onClick={handleLogout} className="btn-secondary w-full text-sm">
          <LogOut className="w-4 h-4" /> ออกจากระบบ
        </button>
      </div>
    </div>
  );
}
